import type { DashboardProvenance } from "./api";
import type { DashboardData } from "./types";

export const OFFLINE_SNAPSHOT_KEY = "horosho.lastVerified.v1";
export const OFFLINE_SNAPSHOT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1_000;
export const OFFLINE_SNAPSHOT_MAX_BYTES = 12 * 1_024;
const FUTURE_CLOCK_SKEW_MS = 5 * 60 * 1_000;
const MAX_HIGHLIGHTS = 12;
const SECRET_KEY_PATTERN = /(auth|browser|context|cookie|credential|live.?view|password|secret|session|telegram|token)/i;
const SAFE_KEY_PATTERN = /^[a-z0-9][a-z0-9_.-]{0,63}$/;
const ALLOWED_STATES = new Set(["live", "delayed", "stale"]);

export type OfflineHighlight = {
  key: string;
  label: string;
  value: string;
  observation_date: string;
  source_at: string;
  status: "live" | "delayed" | "stale";
};

export type OfflineSnapshot = {
  version: 1;
  saved_at: string;
  verified_at: string;
  overview: OfflineHighlight[];
};

export type StorageLike = Pick<Storage, "getItem" | "setItem" | "removeItem">;

function isFiniteDate(value: unknown): value is string {
  return typeof value === "string" && value.length <= 32 && Number.isFinite(Date.parse(value));
}

function isHighlight(value: unknown): value is OfflineHighlight {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const item = value as Record<string, unknown>;
  const keys = Object.keys(item).sort().join(",");
  if (keys !== "key,label,observation_date,source_at,status,value") return false;
  return (
    typeof item.key === "string" &&
    SAFE_KEY_PATTERN.test(item.key) &&
    !SECRET_KEY_PATTERN.test(item.key) &&
    typeof item.label === "string" && item.label.length > 0 && item.label.length <= 80 &&
    typeof item.value === "string" && item.value.length > 0 && item.value.length <= 64 &&
    typeof item.observation_date === "string" && item.observation_date.length > 0 && item.observation_date.length <= 32 &&
    isFiniteDate(item.source_at) &&
    typeof item.status === "string" && ALLOWED_STATES.has(item.status)
  );
}

function validateSnapshot(value: unknown, now: Date): OfflineSnapshot | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const snapshot = value as Record<string, unknown>;
  if (Object.keys(snapshot).sort().join(",") !== "overview,saved_at,verified_at,version") return null;
  if (snapshot.version !== 1 || !isFiniteDate(snapshot.saved_at) || !isFiniteDate(snapshot.verified_at)) return null;
  if (!Array.isArray(snapshot.overview) || snapshot.overview.length === 0 || snapshot.overview.length > MAX_HIGHLIGHTS) return null;
  if (!snapshot.overview.every(isHighlight)) return null;

  const nowMs = now.getTime();
  const verifiedMs = Date.parse(snapshot.verified_at);
  const savedMs = Date.parse(snapshot.saved_at);
  const oldestSourceMs = Math.min(...snapshot.overview.map((item) => Date.parse(item.source_at)));
  if (verifiedMs > nowMs + FUTURE_CLOCK_SKEW_MS || savedMs > nowMs + FUTURE_CLOCK_SKEW_MS) return null;
  if (!Number.isFinite(oldestSourceMs) || verifiedMs !== oldestSourceMs) return null;
  if (nowMs - verifiedMs > OFFLINE_SNAPSHOT_MAX_AGE_MS) return null;
  if (savedMs + FUTURE_CLOCK_SKEW_MS < verifiedMs) return null;
  return snapshot as OfflineSnapshot;
}

export function saveOfflineSnapshot(
  storage: StorageLike,
  value: OfflineSnapshot,
  now = new Date()
): boolean {
  const validated = validateSnapshot(value, now);
  if (!validated) throw new TypeError("Offline snapshot does not match the safe market-data contract");
  const encoded = JSON.stringify(validated);
  if (new TextEncoder().encode(encoded).byteLength > OFFLINE_SNAPSHOT_MAX_BYTES) {
    throw new RangeError("Offline snapshot exceeds the device privacy size limit");
  }
  try {
    storage.setItem(OFFLINE_SNAPSHOT_KEY, encoded);
    return true;
  } catch {
    return false;
  }
}

export function loadOfflineSnapshot(
  storage: StorageLike,
  now = new Date()
): OfflineSnapshot | null {
  const encoded = storage.getItem(OFFLINE_SNAPSHOT_KEY);
  if (!encoded) return null;
  if (new TextEncoder().encode(encoded).byteLength > OFFLINE_SNAPSHOT_MAX_BYTES) {
    clearOfflineSnapshot(storage);
    return null;
  }
  try {
    const snapshot = validateSnapshot(JSON.parse(encoded), now);
    if (!snapshot) clearOfflineSnapshot(storage);
    return snapshot;
  } catch {
    clearOfflineSnapshot(storage);
    return null;
  }
}

export function clearOfflineSnapshot(storage: StorageLike): void {
  try {
    storage.removeItem(OFFLINE_SNAPSHOT_KEY);
  } catch {
    // Privacy cleanup is best effort for disabled or unavailable device storage.
  }
}

function approvedStatus(value: unknown): OfflineHighlight["status"] | null {
  return value === "live" || value === "delayed" || value === "stale" ? value : null;
}

function oldestTimestamp(...values: Array<string | null | undefined>): string | null {
  const timestamps = values
    .filter(isFiniteDate)
    .map((value) => ({ value, time: Date.parse(value) }))
    .sort((left, right) => left.time - right.time);
  return timestamps[0]?.value ?? null;
}

export function offlineSnapshotFromDashboard(
  data: DashboardData,
  provenance: DashboardProvenance,
  now = new Date()
): OfflineSnapshot | null {
  const highlights: OfflineHighlight[] = [];
  const add = (highlight: OfflineHighlight | null) => {
    if (highlight) highlights.push(highlight);
  };

  if (provenance.manufacturing) {
    const series = data.manufacturing.series.find((item) => item.key === "us_oecd_cli");
    const point = series?.points.at(-1);
    const status = approvedStatus(data.manufacturing.oecd_source?.status);
    const sourceAt = oldestTimestamp(data.manufacturing.oecd_source?.collected_at);
    add(series && point && status && sourceAt ? {
      key: "us_oecd_cli",
      label: series.label,
      value: Number.isFinite(point.value) ? String(point.value) : "",
      observation_date: point.date,
      source_at: sourceAt,
      status
    } : null);
  }

  if (provenance.crypto) {
    const btc = data.crypto.btc_spot?.at(-1);
    const status = approvedStatus(data.crypto.btc_spot_source?.status);
    const sourceAt = oldestTimestamp(data.crypto.btc_spot_source?.collected_at);
    add(btc && status && sourceAt && Number.isFinite(btc.value) ? {
      key: "btc_spot",
      label: "BTC Spot",
      value: `$${btc.value.toLocaleString("en-US", { maximumFractionDigits: 0 })}`,
      observation_date: btc.date,
      source_at: sourceAt,
      status
    } : null);
  }

  const verifiedAt = oldestTimestamp(...highlights.map((highlight) => highlight.source_at));
  if (highlights.length === 0 || !verifiedAt) return null;
  return validateSnapshot({
    version: 1,
    saved_at: now.toISOString(),
    verified_at: verifiedAt,
    overview: highlights.slice(0, MAX_HIGHLIGHTS)
  }, now);
}
